import React, { useState, useEffect, useMemo, useRef } from 'react';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import { Link } from 'react-router-dom';
import { Send, ArrowRight, CheckCircle, Calendar, XCircle, AlertTriangle, Search, RefreshCw, Shield, X, Hash, User } from 'react-feather';
import DatePicker from 'react-datepicker'; // Import standard DatePicker
import 'react-datepicker/dist/react-datepicker.css'; // Import datepicker styles
import '../../components/Layout/DatePicker.css'; // Import custom styles if needed for single picker

// Currency mapping (CODE) - Copied from PartnersPage for consistency
const currencyMap: { [key: string]: string } = {
    'UAE': 'AED',
    'KSA': 'SAR',
    'Qatar': 'QAR',
    'Kuwait': 'KWD',
    'Bahrain': 'BHD',
    'Egypt': 'EGP',
    'Oman': 'OMR',
};

// Helper to format currency like in PartnersPage
const formatCurrency = (baseAmount: number, country: string): string => {
    const symbol = currencyMap[country] || '$';
    
    // Simple adjustment factor for demonstration
    let factor = 1;
    switch (country) {
        case 'KSA': factor = 3.75; break; // SAR
        case 'Qatar': factor = 3.64; break; // QAR
        case 'Kuwait': factor = 0.31; break; // KWD (Note: 1 KWD > 1 USD)
        case 'Bahrain': factor = 0.38; break; // BHD (Note: 1 BHD > 1 USD)
        case 'UAE': factor = 3.67; break;  // AED
        case 'Egypt': factor = 47.5; break; // EGP (Approx)
        case 'Oman': factor = 0.38; break;  // OMR (Approx)
        default: factor = 1; break; // Default factor (USD)
    }

    const adjustedAmount = baseAmount * factor;
    return `${symbol} ${Math.round(adjustedAmount).toLocaleString()}`;
};

// Partner Risk Status Badges
const RiskStatusBadge: React.FC<{ status: 'On Hold' | 'High Risk' | 'Medium Risk' | 'Low Risk' }> = ({ status }) => {
  let backgroundColor = '';
  let color = '';
  switch (status) {
    case 'On Hold': backgroundColor = '#fee2e2'; color = '#dc2626'; break; // Red
    case 'High Risk': backgroundColor = '#fef9c3'; color = '#ca8a04'; break; // Yellow/Amber
    case 'Medium Risk': backgroundColor = '#f3f4f6'; color = '#6b7280'; break; // Gray
    case 'Low Risk': backgroundColor = '#d1fae5'; color = '#059669'; break; // Green
  }
  return <span style={{ backgroundColor, color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>{status}</span>;
};

// Status Badge similar to PartnersPage
const StatusBadge: React.FC<{ status: 'Active' | 'Inactive' | 'Pending' }> = ({ status }) => {
  let backgroundColor = '';
  let color = '';
  switch (status) {
    case 'Active': backgroundColor = '#ecfdf5'; color = '#059669'; break; // Lighter Green
    case 'Inactive': backgroundColor = '#f8fafc'; color = '#64748b'; break; // Lighter Gray
    case 'Pending': backgroundColor = '#fefce8'; color = '#d97706'; break; // Lighter Yellow/Amber
  }
  return <span style={{ backgroundColor, color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>{status}</span>;
};

// Risk category widget component
const RiskCategoryWidget: React.FC<{
  category: 'Low Risk' | 'Medium Risk' | 'High Risk';
  score: string;
  action: string;
  count?: number;
}> = ({ category, score, action, count }) => {
  let backgroundColor = '';
  let color = '';
  let borderColor = '';
  let icon: React.ReactNode = null;

  switch (category) {
    case 'Low Risk':
      backgroundColor = '#f0fdf4';
      color = '#059669';
      borderColor = '#dcfce7';
      break;
    case 'Medium Risk':
      backgroundColor = '#fffbeb';
      color = '#d97706';
      borderColor = '#fef3c7';
      break;
    case 'High Risk':
      backgroundColor = '#fef2f2';
      color = '#dc2626';
      borderColor = '#fee2e2';
      icon = (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
      break;
  }

  return (
    <div style={{
      backgroundColor,
      padding: '24px',
      borderRadius: '8px',
      border: `1px solid ${borderColor}`,
      flex: 1,
      minWidth: '250px',
      position: 'relative',
    }}>
      {icon && <div style={{ position: 'absolute', top: '24px', right: '24px', color }}>{icon}</div>}
      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color }}>{category}</h3>
      <div style={{ margin: '0 0 4px 0', fontWeight: '500', color: '#4b5563' }}>Score: {score}</div>
      <div style={{ color: '#6b7280', fontSize: '14px' }}>{action}</div>
      {count !== undefined && (
        <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>
          {count.toLocaleString()} partners
        </div>
      )}
    </div>
  );
};

// Mini bar chart component for risk distribution
const RiskDistributionChart: React.FC<{ data: number[] }> = ({ data }) => {
  const max = Math.max(...data);
  
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '40px', marginTop: '8px' }}>
      {data.map((value, index) => (
        <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div 
            style={{
              width: '100%',
              height: `${(value / max) * 40}px`,
              backgroundColor: index < 4 ? '#ef4444' : (index < 8 ? '#f59e0b' : '#10b981'),
              borderRadius: '2px 2px 0 0',
            }}
          />
          <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>{index * 10}-{(index+1) * 10}%</div>
        </div>
      ))}
    </div>
  );
};

// Enhanced mock data for partners currently on hold
export const mockOnHoldPartners = [
  { 
    id: 'p001', 
    name: 'Talabat', 
    location: 'Qatar',
    products: ['BNPL', 'Acquiring'],
    baseDebit: 410000,
    baseCredit: 75000,
    reason: 'Payment Default',
    date: '2024-07-15',
    endDate: '2024-08-14', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold',
    risk: '59%',
    riskScore: '59%',
    lastPayout: '14 days ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/b/b3/Talabat_logo.svg'
  },
  { 
    id: 'p003', 
    name: 'Raya Store', 
    location: 'Egypt',
    products: ['BNPL', 'Capital'], 
    baseDebit: 120000,
    baseCredit: 25000,
    reason: 'Compliance Breach',
    date: '2024-07-10',
    endDate: '2024-08-09', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold', 
    risk: '63%',
    riskScore: '63%',
    lastPayout: '18 days ago',
    logoUrl: null
  },
  { 
    id: 'p008', 
    name: 'Namshi', 
    location: 'KSA',
    products: ['BNPL', 'Capital'],
    baseDebit: 420000,
    baseCredit: 110000,
    reason: 'Suspicious Transaction Activity',
    date: '2024-07-22',
    endDate: '2024-08-21', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold',
    risk: '65%',
    riskScore: '65%',
    lastPayout: '8 days ago',
    logoUrl: null
  },
  { 
    id: 'p012', 
    name: 'B.Tech', 
    location: 'Egypt',
    products: ['BNPL'],
    baseDebit: 180000,
    baseCredit: 40000,
    reason: 'Multiple Fraud Reports',
    date: '2024-07-18',
    endDate: '2024-08-17', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold',
    risk: '58%',
    riskScore: '58%',
    lastPayout: '12 days ago',
    logoUrl: null
  },
  { 
    id: 'p021', 
    name: 'Virgin Megastore', 
    location: 'UAE',
    products: ['BNPL', 'Tabby Card'],
    baseDebit: 345000,
    baseCredit: 82000,
    reason: 'Regulatory Compliance Issue',
    date: '2024-07-03',
    endDate: '2024-08-02', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold',
    risk: '61%',
    riskScore: '61%',
    lastPayout: '21 days ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/e/ea/Virgin-logo.svg'
  },
  { 
    id: 'p032', 
    name: 'Axiom Telecom', 
    location: 'KSA',
    products: ['BNPL', 'Acquiring', 'Prepaid Ads'],
    baseDebit: 520000,
    baseCredit: 135000,
    reason: 'AML Investigation',
    date: '2024-07-05',
    endDate: '2024-08-04', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold',
    risk: '52%',
    riskScore: '52%',
    lastPayout: '19 days ago',
    logoUrl: null
  },
  { 
    id: 'p045', 
    name: 'Jumbo Electronics', 
    location: 'UAE',
    products: ['BNPL', 'Capital'],
    baseDebit: 630000,
    baseCredit: 180000,
    reason: 'Excessive Chargebacks',
    date: '2024-07-12',
    endDate: '2024-08-11', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold',
    risk: '55%',
    riskScore: '55%',
    lastPayout: '16 days ago',
    logoUrl: 'https://www.jumbo.ae/media/logo/stores/1/jumbo-logo.svg'
  },
  { 
    id: 'p052', 
    name: 'Al Jaber Optical', 
    location: 'UAE',
    products: ['BNPL'],
    baseDebit: 190000,
    baseCredit: 42000,
    reason: 'Fraudulent Transaction Pattern',
    date: '2024-07-08',
    endDate: '2024-08-07', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold',
    risk: '60%',
    riskScore: '60%',
    lastPayout: '17 days ago',
    logoUrl: null
  },
  { 
    id: 'p063', 
    name: 'Extra', 
    location: 'KSA',
    products: ['BNPL', 'Tabby Card'],
    baseDebit: 480000,
    baseCredit: 125000,
    reason: 'Payment Processing Error',
    date: '2024-07-14',
    endDate: '2024-08-13', // Added endDate
    status: 'Inactive',
    riskStatus: 'On Hold',
    risk: '67%',
    riskScore: '67%',
    lastPayout: '15 days ago',
    logoUrl: 'https://m.extra.com/assets/web/bundle/c08a58bfcb2fbbe98fde.svg'
  }
];

// Enhanced mock data for high-risk partners
export const mockHighRiskPartners = [
  { 
    id: 'p002', 
    name: 'Adidas', 
    location: 'UAE',
    products: ['BNPL', 'Tabby Card', 'Postpaid Ads'],
    baseDebit: 380000,
    baseCredit: 70000,
    flag: 'Unusual Transaction Volume',
    lastActivity: '2024-07-20',
    status: 'Pending',
    riskStatus: 'High Risk',
    risk: '75%',
    riskScore: '75%',
    lastPayout: '6 days ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/2/20/Adidas_Logo.svg'
  },
  { 
    id: 'p005', 
    name: 'Sharaf DG', 
    location: 'Bahrain',
    products: ['BNPL', 'Acquiring'],
    baseDebit: 180000,
    baseCredit: 30000,
    flag: 'Multiple Chargebacks',
    lastActivity: '2024-07-18',
    status: 'Pending',
    riskStatus: 'High Risk',
    risk: '70%',
    riskScore: '70%',
    lastPayout: '3 days ago',
    logoUrl: 'https://uae.sharafdg.com/wp-content/uploads/2020/11/logo-sharafdg.png'
  },
  { 
    id: 'p007', 
    name: 'Noon', 
    location: 'UAE',
    products: ['BNPL', 'Prepaid Ads', 'Postpaid Ads'],
    baseDebit: 1200000,
    baseCredit: 450000,
    flag: 'Suspicious Login Patterns',
    lastActivity: '2024-07-21',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '78%',
    riskScore: '78%',
    lastPayout: '3 days ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/ca/Noon_Website_Logo.svg'
  },
  { 
    id: 'p014', 
    name: 'Xcite', 
    location: 'Kuwait',
    products: ['BNPL'],
    baseDebit: 210000,
    baseCredit: 45000,
    flag: 'Rapid Fund Withdrawal',
    lastActivity: '2024-07-19',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '80%',
    riskScore: '80%',
    lastPayout: '1 day ago',
    logoUrl: 'https://media.xcite.com/media/logo/stores/1/New-Xcite-Logo-EnAr.png'
  },
  { 
    id: 'p015', 
    name: 'Extra Stores', 
    location: 'Oman',
    products: ['BNPL'],
    baseDebit: 280000,
    baseCredit: 60000,
    flag: 'Unusual Login Location',
    lastActivity: '2024-07-22',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '72%',
    riskScore: '72%',
    lastPayout: '2 days ago',
    logoUrl: null
  },
  { 
    id: 'p018', 
    name: 'H&M', 
    location: 'Qatar',
    products: ['BNPL', 'Acquiring'],
    baseDebit: 290000,
    baseCredit: 60000,
    flag: 'Velocity Pattern Irregularity',
    lastActivity: '2024-07-23',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '73%',
    riskScore: '73%',
    lastPayout: '4 days ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/53/H%26M-Logo.svg'
  },
  { 
    id: 'p024', 
    name: 'Spinneys', 
    location: 'UAE',
    products: ['BNPL', 'Acquiring'],
    baseDebit: 520000,
    baseCredit: 110000,
    flag: 'Transaction Scoring Threshold',
    lastActivity: '2024-07-21',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '76%',
    riskScore: '76%',
    lastPayout: '5 days ago',
    logoUrl: 'https://www.spinneys.com/en-ae/assets/img/Spinneys_logo.svg'
  },
  { 
    id: 'p027', 
    name: 'Carrefour', 
    location: 'UAE',
    products: ['BNPL', 'Acquiring', 'Tabby Card'],
    baseDebit: 950000,
    baseCredit: 210000,
    flag: 'Multiple Failed Transactions',
    lastActivity: '2024-07-20',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '79%',
    riskScore: '79%',
    lastPayout: '2 days ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/5b/Carrefour_logo.svg'
  },
  { 
    id: 'p033', 
    name: 'IKEA', 
    location: 'KSA',
    products: ['BNPL', 'Capital', 'Prepaid Ads'],
    baseDebit: 730000,
    baseCredit: 160000,
    flag: 'Credit Limit Excess',
    lastActivity: '2024-07-22',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '72%',
    riskScore: '72%',
    lastPayout: '3 days ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/c5/Ikea_logo.svg'
  },
  { 
    id: 'p036', 
    name: 'Jarir Bookstore', 
    location: 'Kuwait',
    products: ['BNPL', 'Capital'],
    baseDebit: 260000,
    baseCredit: 55000,
    flag: 'Return Rate Threshold Exceeded',
    lastActivity: '2024-07-21',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '74%',
    riskScore: '74%',
    lastPayout: '1 day ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/en/5/58/Jarir_Bookstore_logo.svg'
  },
  { 
    id: 'p039', 
    name: 'Sephora', 
    location: 'UAE',
    products: ['BNPL', 'Acquiring'],
    baseDebit: 320000,
    baseCredit: 70000,
    flag: 'Payment Authentication Issues',
    lastActivity: '2024-07-23',
    status: 'Pending',
    riskStatus: 'High Risk',
    risk: '77%',
    riskScore: '77%',
    lastPayout: '4 days ago',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3a/Sephora_logo.svg/2560px-Sephora_logo.svg.png'
  },
  { 
    id: 'p042', 
    name: 'Centrepoint', 
    location: 'UAE',
    products: ['BNPL', 'Tabby Card'],
    baseDebit: 240000,
    baseCredit: 50000,
    flag: 'Merchant Domain Inconsistency',
    lastActivity: '2024-07-21',
    status: 'Active',
    riskStatus: 'High Risk',
    risk: '71%',
    riskScore: '71%',
    lastPayout: '3 days ago',
    logoUrl: null
  }
];

// Modal Backdrop component (similar to PayoutsPage)
interface ModalBackdropProps {
  children: React.ReactNode;
  onClose: () => void;
  preventClose?: boolean;
}

const ModalBackdrop: React.FC<ModalBackdropProps> = ({ children, onClose, preventClose = false }) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the modal content
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (preventClose) return;
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, preventClose]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        ref={modalContentRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          width: '100%',
          maxWidth: '650px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {children}
      </div>
    </div>
  );
};

// Partner Hold Selection Modal
interface PartnerHoldSelectionModalProps {
  onClose: () => void;
  onSelectPartner: (partner: any) => void;
  partners: any[];
}

const PartnerHoldSelectionModal: React.FC<PartnerHoldSelectionModalProps> = ({ onClose, onSelectPartner, partners }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredPartnerId, setHoveredPartnerId] = useState<string | null>(null);

  // Filter partners based on search query
  const filteredPartners = useMemo(() => {
    if (!searchQuery) return partners;
    const lowerCaseQuery = searchQuery.toLowerCase();
    return partners.filter(partner => 
      partner.name.toLowerCase().includes(lowerCaseQuery) || 
      partner.location.toLowerCase().includes(lowerCaseQuery) ||
      partner.riskScore.toLowerCase().includes(lowerCaseQuery)
    );
  }, [partners, searchQuery]);

  // Render product tags (compact version)
  const renderProductTags = (products: string[]) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {products.map(product => {
        // Different colors based on product type
        let color, backgroundColor;
        switch(product) {
          case 'BNPL':
            color = '#0891b2'; // cyan-600
            backgroundColor = '#ecfeff'; // cyan-50
            break;
          case 'Acquiring':
            color = '#0e7490'; // cyan-700
            backgroundColor = '#cffafe'; // cyan-100
            break;
          case 'Tabby Card':
            color = '#0284c7'; // blue-600
            backgroundColor = '#e0f2fe'; // blue-50
            break;
          case 'Postpaid Ads':
            color = '#7c3aed'; // violet-600
            backgroundColor = '#f3e8ff'; // violet-50
            break;
          case 'Prepaid Ads':
            color = '#a855f7'; // purple-500
            backgroundColor = '#f5f3ff'; // purple-50
            break;
          case 'Capital':
            color = '#16a34a'; // green-600
            backgroundColor = '#ecfdf5'; // green-50
            break;
          default:
            color = '#6b7280'; // gray-500
            backgroundColor = '#f3f4f6'; // gray-100
        }
        
        return (
          <span 
            key={product} 
            style={{ 
              fontSize: '10px', 
              padding: '2px 6px', 
              borderRadius: '4px', 
              backgroundColor, 
              color,
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
          >
            {product}
          </span>
        );
      })}
    </div>
  );

  return (
    <>
      {/* Modal Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Put Partner on Hold</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Select partner to place on hold</p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#6b7280',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.15s ease-in-out'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Search input */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input 
            type="text"
            placeholder="Search partners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 38px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Partner List Table */}
      <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f9fafb', zIndex: 1 }}>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ width: '50%', padding: '10px 16px', textAlign: 'left', fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Partner</th>
              <th style={{ width: '30%', padding: '10px 16px', textAlign: 'left', fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Products</th>
              <th style={{ width: '20%', padding: '10px 16px', textAlign: 'right', fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Risk</th>
            </tr>
          </thead>
          <tbody>
            {filteredPartners.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ margin: 0, fontSize: '14px' }}>No partners found matching your search</p>
                </td>
              </tr>
            ) : (
              filteredPartners.map(partner => (
                <tr 
                  key={partner.id}
                  onClick={() => onSelectPartner(partner)}
                  onMouseEnter={() => setHoveredPartnerId(partner.id)}
                  onMouseLeave={() => setHoveredPartnerId(null)}
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    backgroundColor: hoveredPartnerId === partner.id ? '#f9fafb' : 'transparent',
                    transition: 'background-color 0.1s ease-in-out',
                  }}
                >
                  {/* Partner Name & Location Cell */}
                  <td style={{ padding: '10px 16px', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* Partner initials */}
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#4b5563',
                        marginRight: '10px',
                        flexShrink: 0,
                      }}>
                        {partner.name.split(' ').map((n: string) => n?.[0]).filter(Boolean).slice(0, 2).join('')}
                      </div>
                      
                      <div>
                        <div style={{ fontWeight: 500, color: '#111827', marginBottom: '2px' }}>{partner.name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{partner.location}</div>
                      </div>
                    </div>
                  </td>

                  {/* Products Cell */}
                  <td style={{ padding: '10px 16px', verticalAlign: 'middle' }}>
                    {renderProductTags(partner.products)}
                  </td>

                  {/* Risk Score Cell */}
                  <td style={{ padding: '10px 16px', verticalAlign: 'middle', textAlign: 'right' }}>
                    <div style={{ 
                      display: 'inline-block',
                      padding: '3px 8px',
                      backgroundColor: parseInt(partner.riskScore) >= 75 ? '#fef2f2' : '#fffbeb',
                      color: parseInt(partner.riskScore) >= 75 ? '#b91c1c' : '#92400e',
                      borderRadius: '12px', 
                      fontWeight: '500', 
                      fontSize: '12px',
                    }}>
                      {partner.riskScore}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Partner Hold Details Modal
interface PartnerHoldDetailsModalProps {
  onClose: () => void;
  onConfirm: (details: { reason: string; endDate: Date; products: string[] }) => void;
  partner: any;
}

const PartnerHoldDetailsModal: React.FC<PartnerHoldDetailsModalProps> = ({ onClose, onConfirm, partner }) => {
  const [reason, setReason] = useState('');
  // State for single date, default to 7 days from now
  const [endDate, setEndDate] = useState<Date | null>(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [hasError, setHasError] = useState(false);

  // Available products
  const allProducts = ['BNPL', 'Acquiring', 'Credit', 'Tabby Card', 'Ads Postpaid'];

  // Handle product selection toggle
  const toggleProduct = (product: string) => {
    if (selectedProducts.includes(product)) {
      setSelectedProducts(prev => prev.filter(p => p !== product));
    } else {
      setSelectedProducts(prev => [...prev, product]);
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    // Ensure endDate is not null before confirming
    if (!reason || selectedProducts.length === 0 || !endDate) {
      setHasError(true);
      return;
    }
    onConfirm({ reason, endDate, products: selectedProducts });
  };

  // Format date for display
  const formatDate = (date: Date | null): string => {
    if (!date) return 'Select Date'; // Handle null case
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle single date selection
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setEndDate(date);
      setIsDatePickerVisible(false); // Close picker after selection
    }
  };

  // Ref for the date picker container to handle outside clicks
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Effect to handle clicks outside the date picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        // If the click is outside the date picker container, close it.
        setIsDatePickerVisible(false);
      }
    };

    if (isDatePickerVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDatePickerVisible]);

  return (
    <>
      {/* Modal Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Hold Details</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Provide details for holding {partner.name}</p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#6b7280',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.15s ease-in-out'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Form content */}
      <div style={{ padding: '20px 24px', maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
        {/* Partner summary - simplified */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
        }}>
          {/* Partner initials */}
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '500',
            color: '#4b5563',
            marginRight: '12px',
            flexShrink: 0,
          }}>
            {partner.name.split(' ').map((n: string) => n?.[0]).filter(Boolean).slice(0, 2).join('')}
          </div>
          
          <div>
            <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}>{partner.name}</div>
            <div style={{ fontSize: '13px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{partner.location}</span>
              <span style={{
                backgroundColor: '#fef3c7',
                color: '#92400e',
                padding: '1px 6px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 500,
              }}>
                {partner.riskScore}
              </span>
            </div>
          </div>
        </div>

        {/* Hold reason */}
        <div style={{ marginBottom: '20px' }}>
          <label
            htmlFor="holdReason"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}
          >
            Reason for Hold
          </label>
          <textarea
            id="holdReason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (hasError && e.target.value) setHasError(false);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: hasError && !reason ? '1px solid #ef4444' : '1px solid #d1d5db',
              fontSize: '14px',
              minHeight: '80px',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
            placeholder="Enter reason for placing partner on hold..."
          />
          {hasError && !reason && (
            <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>Please provide a reason for the hold</p>
          )}
        </div>

        {/* End date selection */}
        <div style={{ marginBottom: '20px' }}>
          <label
            htmlFor="endDate"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}
          >
            End Date
          </label>
          <div style={{ position: 'relative' }}>
            <button
              id="hold-end-date-button"
              type="button"
              onClick={() => setIsDatePickerVisible(!isDatePickerVisible)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 12px',
                width: '100%',
                borderRadius: '6px',
                border: hasError && !endDate ? '1px solid #ef4444' : '1px solid #d1d5db',
                backgroundColor: 'white',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: '14px',
                color: '#111827',
                boxSizing: 'border-box',
              }}
            >
              <Calendar size={16} style={{ color: '#6b7280', marginRight: '8px' }} />
              <span>{formatDate(endDate)}</span>
            </button>

            {isDatePickerVisible && (
              <div
                ref={datePickerRef}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  zIndex: 10,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                }}>
                <DatePicker
                  selected={endDate}
                  onChange={handleDateChange}
                  inline
                  dateFormat="MMM dd, yyyy"
                  minDate={new Date()}
                  calendarClassName="custom-datepicker-style"
                />
              </div>
            )}
            {hasError && !endDate && (
              <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>Please select an end date</p>
            )}
          </div>
        </div>

        {/* Products on Hold */}
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}
          >
            Products to Put on Hold
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px'
          }}>
            {allProducts.map(product => (
              <div
                key={product}
                onClick={() => toggleProduct(product)}
                style={{
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: selectedProducts.includes(product) ? '#f0f9ff' : 'white',
                  borderColor: selectedProducts.includes(product) ? '#93c5fd' : '#d1d5db',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '4px',
                  border: selectedProducts.includes(product) ? '1px solid #3b82f6' : '1px solid #d1d5db',
                  backgroundColor: selectedProducts.includes(product) ? '#3b82f6' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {selectedProducts.includes(product) && (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24" style={{ width: '12px', height: '12px' }}>
                      <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <span style={{
                  fontSize: '14px',
                  color: '#111827'
                }}>
                  {product}
                </span>
              </div>
            ))}
          </div>
          {hasError && selectedProducts.length === 0 && (
            <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px' }}>Please select at least one product</p>
          )}
        </div>

        {/* Warning message */}
        <div style={{
          marginTop: '20px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          borderRadius: '6px',
          borderLeft: '3px solid #ef4444',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <AlertTriangle size={18} style={{ color: '#dc2626', flexShrink: 0, marginTop: '1px' }} />
          <div>
            <p style={{ margin: '0', fontSize: '13px', fontWeight: '500', color: '#b91c1c' }}>
              Putting a partner on hold will suspend all selected products and services.
              Customers will not be able to use Tabby with this partner until the hold is lifted.
            </p>
          </div>
        </div>
      </div>

      {/* Modal Footer */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        backgroundColor: '#f9fafb'
      }}>
        <button
          onClick={onClose}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            backgroundColor: 'white',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: '#ef4444',
            fontSize: '14px',
            fontWeight: 500,
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
        >
          Put on Hold
          <ArrowRight size={16} />
        </button>
      </div>
    </>
  );
};

// Processing step interface (similar to PayoutsPage)
interface ProcessingStep {
  name: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'processing' | 'success';
}

// Partner Hold Processing Modal
interface PartnerHoldProcessingModalProps {
  onFinish: () => void;
  partner: any;
  holdDetails: { 
    reason: string; 
    endDate: Date; 
    products: string[] 
  };
}

const PartnerHoldProcessingModal: React.FC<PartnerHoldProcessingModalProps> = ({ onFinish, partner, holdDetails }) => {
  const [steps, setSteps] = useState<ProcessingStep[]>([
    {
      name: 'verify',
      label: 'Verifying Partner Status',
      description: 'Checking current partner status and active services',
      icon: <Shield size={18} />,
      status: 'processing'
    },
    {
      name: 'notify',
      label: 'Notifying Systems',
      description: 'Updating all internal systems with hold status',
      icon: <AlertTriangle size={18} />,
      status: 'pending'
    },
    {
      name: 'suspend',
      label: 'Suspending Products',
      description: `Suspending ${holdDetails.products.join(', ')} services`,
      icon: <XCircle size={20} />,
      status: 'pending'
    },
    {
      name: 'schedule',
      label: 'Scheduling Release',
      description: `Setting automatic release for ${holdDetails.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      icon: <Calendar size={20} />,
      status: 'pending'
    }
  ]);

  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [completionTimestamp, setCompletionTimestamp] = useState<string | null>(null);

  // Simulate the processing steps
  useEffect(() => {
    if (currentStep >= steps.length) {
      // All steps are completed
      setTimeout(() => {
        setIsComplete(true);
        setCompletionTimestamp(new Date().toLocaleString());
      }, 1000);
      return;
    }

    // Process the current step
    const timer = setTimeout(() => {
      setSteps(prev => {
        const newSteps = [...prev];
        newSteps[currentStep].status = 'success';
        if (currentStep + 1 < steps.length) {
          newSteps[currentStep + 1].status = 'processing';
        }
        return newSteps;
      });
      setCurrentStep(prev => prev + 1);
    }, 1500); // Each step takes 1.5 seconds

    return () => clearTimeout(timer);
  }, [currentStep, steps.length]);

  return (
    <>
      {/* Modal Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
          {isComplete ? 'Partner On Hold' : 'Processing Hold Request'}
        </h2>
        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
          {isComplete 
            ? `${partner.name} has been successfully put on hold` 
            : 'Please wait while we process the hold request'
          }
        </p>
      </div>

      {/* Modal Body */}
      <div style={{ padding: '24px 32px', minHeight: '300px' }}>
        {isComplete ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#fee2e2', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
                <line x1="12" y1="2" x2="12" y2="12"></line>
              </svg>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
              Partner Successfully Put On Hold
            </h3>
            <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '20px', maxWidth: '450px', margin: '0 auto 20px' }}>
              The selected products for {partner.name} have been suspended until {holdDetails.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.
            </p>
            
            {/* Details container */}
            <div style={{
              backgroundColor: '#f9fafb', 
              border: '1px solid #e5e7eb',
              borderRadius: '8px', 
              padding: '16px', 
              display: 'inline-block',
              textAlign: 'left',
              margin: '0 auto',
              minWidth: '250px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Reference ID</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>HOLD-{Date.now().toString().slice(-9)}</div>
              </div>
               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                 <div style={{ fontSize: '14px', color: '#6b7280' }}>Products on Hold</div>
                 <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>{holdDetails.products.length}</div>
               </div>
               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                 <div style={{ fontSize: '14px', color: '#6b7280' }}>Created At</div>
                 <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>{completionTimestamp}</div>
               </div>
            </div>
          </div>
        ) : (
          <div>
            {steps.map((step, index) => (
              <div 
                key={step.name}
                style={{ 
                  display: 'flex', 
                  marginBottom: index === steps.length - 1 ? 0 : '24px',
                  position: 'relative'
                }}
              >
                {/* Step indicator and icon */}
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 
                    step.status === 'success' ? '#ecfdf5' : 
                    step.status === 'processing' ? '#eff6ff' : 
                    '#f3f4f6',
                  color: 
                    step.status === 'success' ? '#10b981' : 
                    step.status === 'processing' ? '#3b82f6' : 
                    '#9ca3af',
                  marginRight: '16px',
                  flexShrink: 0
                }}>
                  {step.status === 'success' ? (
                    <CheckCircle size={20} />
                  ) : step.status === 'processing' ? (
                    <div style={{ animation: 'spin 2s linear infinite' }}>
                      <RefreshCw size={20} />
                    </div>
                  ) : (
                    step.icon
                  )}
                </div>
                
                {/* Step content */}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    color: 
                      step.status === 'success' ? '#10b981' : 
                      step.status === 'processing' ? '#3b82f6' : 
                      '#6b7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {step.label}
                    {step.status === 'processing' && (
                      <span style={{ fontSize: '14px', marginLeft: '8px', color: '#3b82f6' }}>
                        Processing...
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                    {step.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Footer */}
      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #e5e7eb', 
        display: 'flex', 
        justifyContent: 'flex-end',
        backgroundColor: '#f9fafb'
      }}>
        {isComplete && (
          <button
            onClick={onFinish}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #ef4444',
              backgroundColor: '#ef4444',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
          >
            Close
          </button>
        )}
      </div>

      {/* Add keyframe animation for spinning icon */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

// Partner Risk Details Panel Component
interface RiskDetailsPanelProps {
  partner: any | null;
  onClose: () => void;
  isVisible: boolean;
  contentFadeKey: string;
  onReleaseHold: (partnerId: string) => void;
}

const RiskDetailsPanel: React.FC<RiskDetailsPanelProps> = ({ partner, onClose, isVisible, contentFadeKey, onReleaseHold }) => {
  // Create ref for the panel element
  const panelRef = useRef<HTMLDivElement>(null);
  
  // Add state for content fade animation
  const [contentVisible, setContentVisible] = useState(true);
  
  // Handle partner changes with animation
  useEffect(() => {
    setContentVisible(true);
  }, [contentFadeKey]);
  
  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Skip outside click handling if we clicked on a table row
      if ((event.target as Element).closest('tr')) {
        return;
      }
      
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    // Only add the event listener if the panel is visible
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Early return after hooks are called
  if (!partner) return null;

  // Format dates for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate days until release (if partner is on hold)
  const calculateDaysUntilRelease = () => {
    if (!partner.endDate) return null;
    
    const endDate = new Date(partner.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilRelease = partner.endDate ? calculateDaysUntilRelease() : null;

  return (
    <div 
      ref={panelRef}
      style={{
        position: 'fixed',
        top: '64px', // Adjusted to leave space for header
        right: 0,
        width: '380px',
        height: 'calc(100vh - 64px)', // Adjusted height to account for header
        backgroundColor: 'white',
        boxShadow: '-4px 0 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        overflowY: 'auto',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease-out',
        padding: '24px',
      }}
    >
      {/* Header with close button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: '24px', 
          fontWeight: 600, 
          color: '#111827',
          fontFamily: 'inherit'
        }}>
          Risk Details
        </h2>
        <button 
          onClick={onClose} 
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Panel Content with Fade Animation */}
      <div style={{
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 0.15s ease-in-out',
      }}>
        {/* Risk Status Badge */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          {partner.riskStatus === 'On Hold' && daysUntilRelease !== null && daysUntilRelease < 0 ? (
            <span style={{
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: 'inherit',
            }}>
              Overdue
            </span>
          ) : (
            <RiskStatusBadge status={partner.riskStatus} />
          )}
        </div>

        {/* Amount Section */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ 
            color: '#6b7280', 
            fontSize: '16px', 
            margin: '0 0 8px 0',
            fontWeight: 500,
            fontFamily: 'inherit'
          }}>
            Amount
          </p>
          <h3 style={{ 
            fontSize: '32px', 
            fontWeight: 600, 
            margin: 0,
            color: '#111827',
            fontFamily: 'inherit'
          }}>
            {formatCurrency(partner.baseDebit, partner.location)}
          </h3>
        </div>

        {/* Due Date & Status Indicator (for Partners on Hold) */}
        {partner.riskStatus === 'On Hold' && partner.endDate && (
          <div style={{ 
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <Calendar size={20} color="#6b7280" />
            <div>
              <p style={{ 
                fontSize: '14px', 
                margin: '0 0 4px 0',
                fontFamily: 'inherit',
                color: '#6b7280',
              }}>
                Hold End Date
              </p>
              <div style={{ 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                {formatDate(partner.endDate)}
                {daysUntilRelease !== null && (
                  <span style={{
                    fontSize: '13px',
                    padding: '2px 6px',
                    borderRadius: '12px',
                    backgroundColor: daysUntilRelease < 0 ? '#fee2e2' : 
                                    daysUntilRelease <= 7 ? '#fef3c7' : '#f3f4f6',
                    color: daysUntilRelease < 0 ? '#dc2626' : 
                          daysUntilRelease <= 7 ? '#92400e' : '#6b7280',
                    fontWeight: 500,
                  }}>
                    {daysUntilRelease < 0 ? `${Math.abs(daysUntilRelease)} days overdue` : 
                    daysUntilRelease === 0 ? 'Due today' : 
                    `${daysUntilRelease} days left`}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Divider */}
        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '24px 0' }}></div>

        {/* Partner Details */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Partner</p>
              <div style={{ 
                color: '#111827', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit'
              }}>
                {partner.name}
              </div>
            </div>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Country</p>
              <div style={{ 
                color: '#111827', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit',
              }}>
                {partner.location}
              </div>
            </div>
          </div>

          {/* Dates for Partners on Hold */}
          {partner.riskStatus === 'On Hold' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ width: '48%' }}>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Hold Start Date</p>
                <div style={{ 
                  color: '#111827', 
                  fontSize: '15px', 
                  fontWeight: 500,
                  fontFamily: 'inherit'
                }}>
                  {formatDate(partner.date)}
                </div>
              </div>
              <div style={{ width: '48%' }}>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Hold End Date</p>
                <div style={{ 
                  color: '#111827', 
                  fontSize: '15px', 
                  fontWeight: 500,
                  fontFamily: 'inherit'
                }}>
                  {formatDate(partner.endDate)}
                </div>
              </div>
            </div>
          )}

          {/* Risk Flag for High Risk Partners */}
          {partner.riskStatus === 'High Risk' && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Risk Flag</p>
              <div style={{ 
                color: '#dc2626', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit'
              }}>
                {partner.flag}
              </div>
            </div>
          )}

          {/* Reason for Hold */}
          {partner.riskStatus === 'On Hold' && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Reason for Hold</p>
              <div style={{ 
                color: '#dc2626', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit'
              }}>
                {partner.reason}
              </div>
            </div>
          )}

          {/* Products */}
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Products</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {partner.products.map((product: string) => (
                <span 
                  key={product} 
                  style={{ 
                    backgroundColor: '#f3f4f6', 
                    color: '#374151', 
                    padding: '3px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px', 
                    display: 'inline-block', 
                    marginBottom: '4px' 
                  }}
                >
                  {product}
                </span>
              ))}
            </div>
          </div>

          {/* Tabby User who put partner on hold */}
          {partner.riskStatus === 'On Hold' && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Hold Initiated By</p>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                color: '#111827',
                fontSize: '15px',
                fontWeight: 500,
                fontFamily: 'inherit'
              }}>
                <User size={16} color="#6b7280" />
                <span>Tabby Admin</span>
              </div>
            </div>
          )}
        </div>

        {/* Partner on Hold: Release Button */}
        {partner.riskStatus === 'On Hold' && (
          <div style={{ marginTop: '32px' }}>
            <button
              onClick={() => onReleaseHold(partner.id)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: '#10b981',
                color: '#fff',
                border: 'none',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.15s ease-in-out',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
            >
              Release from Hold
              <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* High Risk Partner: Put on Hold Button */}
        {partner.riskStatus === 'High Risk' && (
          <div style={{ marginTop: '32px' }}>
            <button
              onClick={() => {/* Handle put on hold */}}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: '#ef4444',
                color: '#fff',
                border: 'none',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.15s ease-in-out',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
            >
              Put on Hold
              <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Partner ID */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginTop: '40px',
          color: '#9ca3af',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}>
          <Hash size={16} style={{ marginRight: '6px' }} />
          Partner ID: {partner.id}
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal for Releasing Hold
interface ReleaseConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  partnerName: string;
}

const ReleaseConfirmationModal: React.FC<ReleaseConfirmationModalProps> = ({ isOpen, onClose, onConfirm, partnerName }) => {
  if (!isOpen) return null;

  return (
    <ModalBackdrop onClose={onClose}>
      {/* Modal Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Confirm Release</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Are you sure you want to release this partner?</p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#6b7280',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.15s ease-in-out'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Modal Body */}
      <div style={{ padding: '24px 32px', minHeight: '100px' }}>
         <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '50%', 
            backgroundColor: '#d1fae5', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <CheckCircle size={24} color="#059669" />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '15px', color: '#374151', lineHeight: '1.5' }}>
              Releasing <strong style={{ color: '#111827' }}>{partnerName}</strong> from hold will reactivate their services immediately.
              Please ensure this action is intended.
            </p>
          </div>
        </div>
      </div>

      {/* Modal Footer */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        backgroundColor: '#f9fafb'
      }}>
        <button
          onClick={onClose}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            backgroundColor: 'white',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#10b981', // Green for confirm release
            fontSize: '14px',
            fontWeight: 500,
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
        >
          Confirm Release
          <CheckCircle size={16} />
        </button>
      </div>
    </ModalBackdrop>
  );
};

const RiskManagementPage: React.FC = () => {
  const [partnersOnHold, setPartnersOnHold] = useState([...mockOnHoldPartners]);
  const [highRiskPartnersList, setHighRiskPartnersList] = useState([...mockHighRiskPartners]);
  const partnersOnHoldCount = partnersOnHold.length;
  const highRiskPartnersCount = highRiskPartnersList.length;
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<string | null>(null);
  
  // Calculate additional statistics for the widgets
  const totalDebitExposure = [...partnersOnHold, ...highRiskPartnersList].reduce((sum, partner) => sum + partner.baseDebit, 0);
  const totalCreditExposure = [...partnersOnHold, ...highRiskPartnersList].reduce((sum, partner) => sum + partner.baseCredit, 0);
  const averageRiskScore = (partnersOnHoldCount + highRiskPartnersCount > 0) 
    ? [...partnersOnHold, ...highRiskPartnersList]
      .reduce((sum, partner) => sum + parseInt(partner.riskScore), 0) / 
      (partnersOnHoldCount + highRiskPartnersCount)
    : 0;
  
  // Partner count distribution for risk categories
  const totalPartners = 60000;
  const highRiskPartnersCountForWidget = 3000; // 5% of total
  const mediumRiskPartnersCountForWidget = 15000; // 25% of total
  const lowRiskPartnersCountForWidget = totalPartners - highRiskPartnersCountForWidget - mediumRiskPartnersCountForWidget; // 70% of total
  
  // Region distribution of high-risk partners
  const regionCounts = highRiskPartnersList.reduce((counts: {[key: string]: number}, partner) => {
    counts[partner.location] = (counts[partner.location] || 0) + 1;
    return counts;
  }, {});
  
  // Risk score distribution for visualization
  const riskScoreDistribution = Array(10).fill(0); // 0-10%, 10-20%, ..., 90-100%
  [...partnersOnHold, ...highRiskPartnersList].forEach(partner => {
    const score = parseInt(partner.riskScore);
    const index = Math.min(Math.floor(score / 10), 9); // Ensure it fits in our 0-9 array
    riskScoreDistribution[index]++;
  });
  
  // Find region with most high-risk partners
  const highestRiskRegion = Object.entries(regionCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
  
  // Add state for active tab
  const [activeTab, setActiveTab] = React.useState<'Partners on Hold' | 'High-Risk Partners'>('Partners on Hold');

  // State for main modals
  const [isHoldSelectionModalOpen, setIsHoldSelectionModalOpen] = useState(false);
  const [isHoldDetailsModalOpen, setIsHoldDetailsModalOpen] = useState(false);
  const [isHoldProcessingModalOpen, setIsHoldProcessingModalOpen] = useState(false);
  
  // State for selected partner and hold details
  const [selectedPartnerForAction, setSelectedPartnerForAction] = useState<any | null>(null);
  const [holdDetails, setHoldDetails] = useState<{ reason: string; endDate: Date; products: string[] } | null>(null);
  
  // State for details panel
  const [selectedPartnerForPanel, setSelectedPartnerForPanel] = useState<any | null>(null);
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
  const [contentFadeKey, setContentFadeKey] = useState<string>('');

  // NEW: State for release confirmation modal
  const [isReleaseConfirmModalOpen, setIsReleaseConfirmModalOpen] = useState(false);
  const [partnerToReleaseId, setPartnerToReleaseId] = useState<string | null>(null);

  // NEW: Combined list of high-risk partners for selection
  const partnersForSelection = useMemo(() => {
    return [...highRiskPartnersList];
  }, [highRiskPartnersList]);

  // Update handlePutOnHold to show modal
  const handlePutOnHold = () => {
    setIsHoldSelectionModalOpen(true);
  };

  // Handle partner selection for putting on hold
  const handleSelectPartner = (partner: any) => {
    setSelectedPartnerForAction(partner);
    setIsHoldSelectionModalOpen(false);
    setIsHoldDetailsModalOpen(true);
  };

  // Handle hold details submission
  const handleSubmitHoldDetails = (details: { reason: string; endDate: Date; products: string[] }) => {
    setHoldDetails(details);
    setIsHoldDetailsModalOpen(false);
    setIsHoldProcessingModalOpen(true);
  };

  // Format date nicely
  const formatShortDate = (dateString: string | Date): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle completion of the hold process
  const handleHoldProcessComplete = () => {
    if (selectedPartnerForAction && holdDetails) {
      // Create a new partner on hold entry
      const newOnHoldPartner = {
        ...selectedPartnerForAction,
        reason: holdDetails.reason,
        date: new Date().toISOString().split('T')[0], // Start date is today
        endDate: holdDetails.endDate.toISOString().split('T')[0], // Add end date
        status: 'Inactive',
        riskStatus: 'On Hold',
        products: holdDetails.products // Use selected products
      };
      
      // Update partner lists: remove from high risk and add to on hold
      setHighRiskPartnersList(prev => prev.filter(p => p.id !== selectedPartnerForAction.id));
      setPartnersOnHold(prev => [newOnHoldPartner, ...prev]);
    }
    
    // Close modal and reset state
    setIsHoldProcessingModalOpen(false);
    setSelectedPartnerForAction(null);
    setHoldDetails(null);
  };

  // Render products tags
  const renderProducts = (products: string[]) => {
    return products.map(p => (
      <span 
        key={p} 
        style={{ 
          backgroundColor: '#f3f4f6', 
          color: '#374151', 
          padding: '3px 8px', 
          borderRadius: '4px', 
          marginRight: '5px', 
          fontSize: '12px', 
          display: 'inline-block', 
          marginBottom: '4px' 
        }}
      >
        {p}
      </span>
    ));
  };

  // Render partner logo/initials and name
  const renderPartner = (partner: any) => {
    const partnerUrl = `/partners/${partner.name.toLowerCase().replace(/\\s+/g, '-')}`;
    
    return (
      <Link to={partnerUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
        {partner.logoUrl ? (
          <img
            src={partner.logoUrl}
            alt={`${partner.name} logo`}
            style={{ 
              marginRight: '10px', 
              width: '24px', 
              height: '24px', 
              borderRadius: '50%', 
              objectFit: 'contain', 
              border: '1px solid #f3f4f6' 
            }}
            onError={(e) => {
              const imgElement = e.target as HTMLImageElement;
              imgElement.style.display = 'none';
              const fallbackSpan = imgElement.nextElementSibling as HTMLElement | null;
              if (fallbackSpan && fallbackSpan.classList.contains('logo-fallback')) {
                fallbackSpan.style.display = 'flex';
                fallbackSpan.style.marginRight = '10px';
              }
            }}
          />
        ) : null}
        <span
          className="logo-fallback"
          style={{
            marginRight: '10px',
            display: partner.logoUrl ? 'none' : 'flex',
            width: '24px', 
            height: '24px', 
            borderRadius: '50%', 
            backgroundColor: '#f3f4f6',
            alignItems: 'center', 
            justifyContent: 'center', 
            fontSize: '10px', 
            fontWeight: '500', 
            color: '#6b7280', 
            textTransform: 'uppercase'
          }}
        >
          {partner.name.split(' ').map((n: string) => n?.[0]).filter(Boolean).slice(0, 2).join('')}
        </span>
        <div>
          <div style={{ fontWeight: 500, color: '#111827' }}>{partner.name}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{partner.location}</div>
        </div>
      </Link>
    );
  };

  // --- Handler for row click to show details panel ---
  const handleRowClick = (partner: any) => {
    if (selectedPartnerForPanel && selectedPartnerForPanel.id === partner.id) {
      // If clicking the same partner, close the panel
      handleCloseDetailsPanel();
    } else if (selectedPartnerForPanel) {
      // If a different partner is selected (panel is open), update the partner
      setContentFadeKey(partner.id);
      setSelectedPartnerForPanel(partner);
    } else {
      // First time opening the panel
      setSelectedPartnerForPanel(partner);
      setContentFadeKey(partner.id);
      setTimeout(() => setIsPanelVisible(true), 10);
    }
  };

  // --- Handler to close the details panel ---
  const handleCloseDetailsPanel = () => {
    setIsPanelVisible(false);
    setTimeout(() => setSelectedPartnerForPanel(null), 200);
  };

  // --- Handler to initiate release process (open confirmation modal) ---
  const handleInitiateRelease = (partnerId: string) => {
    setPartnerToReleaseId(partnerId);
    setIsReleaseConfirmModalOpen(true);
  };

  // --- Handler to confirm and execute release ---
  const confirmReleaseHold = () => {
    if (!partnerToReleaseId) return;

    const partnerIndex = partnersOnHold.findIndex(p => p.id === partnerToReleaseId);
    
    if (partnerIndex !== -1) {
      const partner = partnersOnHold[partnerIndex];
      
      // Simulate release: Move to medium risk for demonstration
      const releasedPartner = {
        ...partner,
        status: 'Active', // Set status to Active
        riskStatus: 'Medium Risk', // Change risk status
        risk: '40%', // Adjust risk score/percentage
        riskScore: '40%',
        flag: 'Released from Hold', // Add a flag
        lastActivity: new Date().toISOString().split('T')[0], // Update last activity
        // Remove hold-specific properties
        reason: undefined,
        date: undefined,
        endDate: undefined,
      };
      
      // Update state: remove from on-hold, add to high-risk (or another list if needed)
      setPartnersOnHold(prev => prev.filter(p => p.id !== partnerToReleaseId));
      setHighRiskPartnersList(prev => [releasedPartner, ...prev]); // Add to high-risk for visibility

      // Close the panel and the confirmation modal
      handleCloseDetailsPanel(); // Ensure this closes the details panel
      setIsReleaseConfirmModalOpen(false);
      setPartnerToReleaseId(null);
      
      // Optional: Show notification
      // alert(`${partner.name} has been released from hold.`); // REMOVED THIS ALERT
    } else {
        // Handle case where partner not found (shouldn't happen)
        setIsReleaseConfirmModalOpen(false);
        setPartnerToReleaseId(null);
    }
  };

  // Update renderPartnersOnHoldTable to use correct state and click handler
  const renderPartnersOnHoldTable = () => (
    <section style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead style={{ backgroundColor: '#f9fafb' }}>
          <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
            <th style={{ fontWeight: 500, padding: '8px 24px' }}>Partner</th>
            <th style={{ fontWeight: 500, padding: '8px 8px' }}>Products</th>
            <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'right' }}>Debit Balance</th>
            <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'right' }}>Credit Balance</th>
            <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'center' }}>Status</th>
            <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'center' }}>Risk Score</th>
            <th style={{ fontWeight: 500, padding: '8px 8px' }}>Reason for Hold</th>
            <th style={{ fontWeight: 500, padding: '8px 8px' }}>Hold Start Date</th> 
            <th style={{ fontWeight: 500, padding: '8px 8px' }}>Hold End Date</th> 
          </tr>
        </thead>
        <tbody>
          {partnersOnHold.map((partner) => (
            <tr 
              key={partner.id}
              style={{ 
                borderBottom: '1px solid #f3f4f6',
                backgroundColor: hoveredRowIndex === partner.id ? '#f9fafb' : (selectedPartnerForPanel?.id === partner.id ? '#eff6ff' : 'transparent'), // Highlight selected row
                transition: 'background-color 0.1s ease-in-out',
                cursor: 'pointer'
              }}
              onMouseEnter={() => setHoveredRowIndex(partner.id)}
              onMouseLeave={() => setHoveredRowIndex(null)}
              onClick={() => handleRowClick(partner)} // Use panel handler
            >
              <td style={{ padding: '12px 8px 12px 24px' }}>{renderPartner(partner)}</td>
              <td style={{ padding: '12px 8px' }}>{renderProducts(partner.products)}</td>
              <td style={{ padding: '12px 8px', textAlign: 'right', color: '#111827' }}>{formatCurrency(partner.baseDebit, partner.location)}</td>
              <td style={{ padding: '12px 8px', textAlign: 'right', color: '#111827' }}>{formatCurrency(partner.baseCredit, partner.location)}</td>
              <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                <StatusBadge status={partner.status as 'Active' | 'Inactive' | 'Pending'} />
              </td>
              <td style={{ padding: '12px 8px', textAlign: 'center', color: parseInt(partner.riskScore) >= 90 ? '#059669' : (parseInt(partner.riskScore) < 70 ? '#f43f5e' : '#4b5563') }}>
                {partner.riskScore}
              </td>
              <td style={{ padding: '12px 8px', color: '#dc2626', fontWeight: '500' }}>{partner.reason}</td>
              <td style={{ padding: '12px 8px', color: '#6b7280' }}>{formatShortDate(partner.date)}</td> 
              <td style={{ padding: '12px 8px', color: '#6b7280' }}>{formatShortDate(partner.endDate)}</td> 
            </tr>
          ))}
          {partnersOnHold.length === 0 && (
            <tr>
              <td colSpan={9} style={{ textAlign: 'center', padding: '40px 24px', color: '#6b7280' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px', color: '#d1d5db' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><circle cx="11.5" cy="11.5" r="2.5"></circle><line x1="13.5" y1="13.5" x2="17.5" y2="17.5"></line>
                </svg>
                <p style={{ fontSize: '14px', fontWeight: '500', margin: 0, color: '#111827' }}>No partners currently on hold</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );

  // Update renderHighRiskPartnersTable to use correct state and click handler
  const renderHighRiskPartnersTable = () => (
    <section style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead style={{ backgroundColor: '#f9fafb' }}>
          <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
            <th style={{ fontWeight: 500, padding: '8px 24px' }}>Partner</th>
            <th style={{ fontWeight: 500, padding: '8px 8px' }}>Products</th>
            <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'right' }}>Debit Balance</th>
            <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'right' }}>Credit Balance</th>
            <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'center' }}>Status</th>
            <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'center' }}>Risk Score</th>
            <th style={{ fontWeight: 500, padding: '8px 8px' }}>Risk Flag</th>
            <th style={{ fontWeight: 500, padding: '8px 8px' }}>Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {highRiskPartnersList.map((partner) => (
            <tr 
              key={partner.id}
              style={{ 
                borderBottom: '1px solid #f3f4f6',
                backgroundColor: hoveredRowIndex === partner.id ? '#f9fafb' : (selectedPartnerForPanel?.id === partner.id ? '#eff6ff' : 'transparent'), // Highlight selected row
                transition: 'background-color 0.1s ease-in-out',
                cursor: 'pointer'
              }}
              onMouseEnter={() => setHoveredRowIndex(partner.id)}
              onMouseLeave={() => setHoveredRowIndex(null)}
              onClick={() => handleRowClick(partner)} // Use panel handler
            >
              <td style={{ padding: '12px 8px 12px 24px' }}>{renderPartner(partner)}</td>
              <td style={{ padding: '12px 8px' }}>{renderProducts(partner.products)}</td>
              <td style={{ padding: '12px 8px', textAlign: 'right', color: '#111827' }}>{formatCurrency(partner.baseDebit, partner.location)}</td>
              <td style={{ padding: '12px 8px', textAlign: 'right', color: '#111827' }}>{formatCurrency(partner.baseCredit, partner.location)}</td>
              <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                <StatusBadge status={partner.status as 'Active' | 'Inactive' | 'Pending'} />
              </td>
              <td style={{ padding: '12px 8px', textAlign: 'center', color: parseInt(partner.riskScore) >= 90 ? '#059669' : (parseInt(partner.riskScore) < 70 ? '#f43f5e' : '#4b5563') }}>
                {partner.riskScore}
              </td>
              <td style={{ padding: '12px 8px', color: '#ca8a04', fontWeight: '500' }}>{partner.flag}</td>
              <td style={{ padding: '12px 8px', color: '#6b7280' }}>{formatShortDate(partner.lastActivity)}</td>
            </tr>
          ))}
          {highRiskPartnersList.length === 0 && (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center', padding: '40px 24px', color: '#6b7280' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px', color: '#d1d5db' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><circle cx="11.5" cy="11.5" r="2.5"></circle><line x1="13.5" y1="13.5" x2="17.5" y2="17.5"></line>
                </svg>
                <p style={{ fontSize: '14px', fontWeight: '500', margin: 0, color: '#111827' }}>No high-risk partners found</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        {/* Header Area */}
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Text container */}
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600', color: '#111827', lineHeight: '1.2' }}>Risk Management</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>Monitor and manage partner risk levels</p>
          </div>
          {/* Action Button */}
          <button
            onClick={handlePutOnHold}
            style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#ffffff',
                backgroundColor: '#ef4444', // Keep red for risk action
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                transition: 'background-color 0.15s ease-in-out',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')} // Darker red on hover
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#ef4444')} // Original red
          >
            Put Partner on Hold
          </button>
        </div>

        {/* Risk Category Widgets */}
        <section style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <RiskCategoryWidget 
            category="Low Risk" 
            score="75+" 
            action="No action required"
            count={lowRiskPartnersCountForWidget}
          />
          <RiskCategoryWidget 
            category="Medium Risk" 
            score="50+" 
            action="Monitor closely"
            count={mediumRiskPartnersCountForWidget}
          />
          <RiskCategoryWidget 
            category="High Risk" 
            score="25+" 
            action="Consider hold"
            count={highRiskPartnersCountForWidget}
          />
        </section>

        {/* Summary Statistics */}
        <section style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            flex: 1,
            minWidth: '200px',
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>Partners Currently On Hold</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '600', color: '#dc2626' }}>{partnersOnHoldCount}</p>
          </div>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            flex: 1,
            minWidth: '200px',
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>Active Partners Flagged High-Risk</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '600', color: '#ca8a04' }}>{highRiskPartnersCount}</p>
          </div>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            flex: 1,
            minWidth: '200px',
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>Average Risk Score</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '600', color: '#4b5563' }}>{averageRiskScore.toFixed(1)}%</p>
          </div>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            flex: 1,
            minWidth: '200px',
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>Total Debit Exposure</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '600', color: '#4b5563' }}>${(totalDebitExposure/1000000).toFixed(2)}M</p>
          </div>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            flex: 1,
            minWidth: '200px',
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>Highest Risk Region</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '600', color: '#4b5563' }}>{highestRiskRegion}</p>
          </div>
        </section>

        {/* Risk Distribution Chart */}
        <section style={{ marginBottom: '24px' }}>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Risk Score Distribution</h3>
            <RiskDistributionChart data={riskScoreDistribution} />
          </div>
        </section>

        {/* Tab Navigation */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          overflow: 'hidden', 
          border: '1px solid #e5e7eb',
          marginBottom: '24px' 
        }}>
          <div style={{ borderBottom: '1px solid #f1f5f9' }}>
            <nav style={{ display: 'flex', gap: '8px', padding: '0 16px' }}>
              {(['Partners on Hold', 'High-Risk Partners'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '16px 12px',
                    margin: '0',
                    border: 'none',
                    background: 'none',
                    fontSize: '14px',
                    fontWeight: activeTab === tab ? 600 : 500,
                    color: activeTab === tab ? '#0f172a' : '#64748b',
                    cursor: 'pointer',
                    borderBottom: activeTab === tab ? '2px solid #4f46e5' : '2px solid transparent',
                    marginBottom: '-1px',
                    transition: 'color 0.2s ease',
                  }}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div style={{ padding: '24px' }}>
            {/* Partners on Hold Tab */}
            <div style={{ display: activeTab === 'Partners on Hold' ? 'block' : 'none' }}>
              {renderPartnersOnHoldTable()}
            </div>

            {/* High-Risk Partners Tab */}
            <div style={{ display: activeTab === 'High-Risk Partners' ? 'block' : 'none' }}>
              {renderHighRiskPartnersTable()}
            </div>
          </div>
        </div>

        {/* Modals */}
        {isHoldSelectionModalOpen && (
          <ModalBackdrop onClose={() => setIsHoldSelectionModalOpen(false)}>
            <PartnerHoldSelectionModal 
              onClose={() => setIsHoldSelectionModalOpen(false)}
              onSelectPartner={handleSelectPartner}
              partners={partnersForSelection}
            />
          </ModalBackdrop>
        )}

        {isHoldDetailsModalOpen && selectedPartnerForAction && (
          <ModalBackdrop onClose={() => setIsHoldDetailsModalOpen(false)}>
            <PartnerHoldDetailsModal 
              onClose={() => setIsHoldDetailsModalOpen(false)}
              onConfirm={handleSubmitHoldDetails}
              partner={selectedPartnerForAction}
            />
          </ModalBackdrop>
        )}

        {isHoldProcessingModalOpen && selectedPartnerForAction && holdDetails && (
          <ModalBackdrop onClose={() => {}} preventClose={true}>
            <PartnerHoldProcessingModal 
              onFinish={handleHoldProcessComplete}
              partner={selectedPartnerForAction}
              holdDetails={holdDetails}
            />
          </ModalBackdrop>
        )}

        {/* NEW: Render Release Confirmation Modal */}
        {isReleaseConfirmModalOpen && partnerToReleaseId && (
          <ReleaseConfirmationModal
            isOpen={isReleaseConfirmModalOpen}
            onClose={() => setIsReleaseConfirmModalOpen(false)}
            onConfirm={confirmReleaseHold}
            partnerName={partnersOnHold.find(p => p.id === partnerToReleaseId)?.name || 'Partner'}
          />
        )}

        {/* Details Panel */}
        {selectedPartnerForPanel && (
          <RiskDetailsPanel 
            partner={selectedPartnerForPanel} 
            onClose={handleCloseDetailsPanel}
            isVisible={isPanelVisible}
            contentFadeKey={contentFadeKey}
            onReleaseHold={handleInitiateRelease} // Use the new handler
          />
        )}
      </div>
    </MainLayout>
  );
};

export default RiskManagementPage;
